export type GamepadDrivingInput = {
  connected: boolean
  id: string
  steer: number
  drive: number
  lookX: number
  lookY: number
  handbrake: boolean
  resetPressed: boolean
  startPressed: boolean
  hapticsAvailable: boolean
}

type WebGamepadHapticActuator = {
  effects?: readonly string[]
  playEffect: (type: 'dual-rumble', parameters: {
    startDelay?: number
    duration?: number
    strongMagnitude?: number
    weakMagnitude?: number
  }) => Promise<unknown>
}

type HapticGamepad = Gamepad & {
  vibrationActuator?: WebGamepadHapticActuator | null
}

const disconnectedInput: GamepadDrivingInput = {
  connected: false,
  id: '',
  steer: 0,
  drive: 0,
  lookX: 0,
  lookY: 0,
  handbrake: false,
  resetPressed: false,
  startPressed: false,
  hapticsAvailable: false,
}

function deadzone(value: number, threshold = 0.12) {
  const magnitude = Math.abs(value)
  if (magnitude <= threshold) return 0
  return Math.sign(value) * (magnitude - threshold) / (1 - threshold)
}

function clamp01(value: number) {
  return Math.max(0, Math.min(1, value))
}

function smoothstep(edge0: number, edge1: number, value: number) {
  const t = clamp01((value - edge0) / Math.max(edge1 - edge0, 0.0001))
  return t * t * (3 - 2 * t)
}

export function calculateTyreRumble(
  frontSlipDegrees: number,
  rearSlipDegrees: number,
  peakSlipDegrees: number,
  longitudinalSpeedMetersPerSecond: number,
  lateralSpeedMetersPerSecond: number,
  surface: 'asphalt' | 'grass' | 'air',
  longitudinalAccelerationMetersPerSecondSquared = 0,
  pedalDemand = 0,
  brakingDemand = 0,
  frontCombinedSlipUsage?: number,
  rearCombinedSlipUsage?: number,
) {
  const peakSlip = Math.max(peakSlipDegrees, 0.1)
  const frontUsage = frontCombinedSlipUsage ?? Math.abs(frontSlipDegrees) / peakSlip
  const rearUsage = rearCombinedSlipUsage ?? Math.abs(rearSlipDegrees) / peakSlip
  const dominantUsage = Math.max(frontUsage, rearUsage)

  // Ordinary tyre loading stays quiet until 25% of peak slip, then reaches a
  // gentle 18% rumble at the peak-grip angle. Post-peak breakaway ramps to 60%.
  const gripRumble = 0.18 * smoothstep(0.25, 1, dominantUsage)
  const breakaway = smoothstep(1, 1.35, dominantUsage)
  let requestedRumble = gripRumble + (0.6 - gripRumble) * breakaway

  // Pronounced front saturation is understeer, even if the rear remains calm.
  if (frontUsage >= 1.15 && frontUsage > rearUsage * 1.05) requestedRumble = Math.max(requestedRumble, 0.6)

  // A rear breakaway only becomes a powerslide as the whole car develops
  // sideslip. That prevents one noisy tyre-slip sample from producing 100%.
  const bodySlipDegrees = Math.abs(Math.atan2(
    lateralSpeedMetersPerSecond,
    Math.max(Math.abs(longitudinalSpeedMetersPerSecond), 1),
  )) * 180 / Math.PI
  const powerslide = smoothstep(1, 1.35, rearUsage)
    * smoothstep(6, 22, bodySlipDegrees)
    * smoothstep(3, 8, Math.abs(longitudinalSpeedMetersPerSecond))
  requestedRumble += (1 - requestedRumble) * powerslide

  const movementGate = smoothstep(0.4, 3, Math.hypot(longitudinalSpeedMetersPerSecond, lateralSpeedMetersPerSecond))
  requestedRumble *= movementGate

  // Pedals add deliberately restrained chassis feedback. Braking is stronger
  // than acceleration, while tyre breakaway and terrain remain authoritative.
  const accelerationFeedback = 0.12 * clamp01(pedalDemand)
    * smoothstep(0.5, 5, Math.abs(longitudinalAccelerationMetersPerSecondSquared))
  const brakingFeedback = 0.24 * clamp01(brakingDemand)
    * smoothstep(0.5, 8, Math.max(0, -longitudinalAccelerationMetersPerSecondSquared))
  requestedRumble = Math.max(requestedRumble, accelerationFeedback, brakingFeedback)

  // The world owns the surface label; moving through grass supplies a 50% floor.
  if (surface === 'grass') requestedRumble = Math.max(requestedRumble, 0.5 * movementGate)
  return clamp01(requestedRumble)
}

export class GamepadController {
  private activeIndex: number | null = null
  private resetWasPressed = false
  private startWasPressed = false
  private nextContinuousHapticTime = 0
  private impactHapticUntil = 0
  private smoothedTyreRumble = 0

  poll(): GamepadDrivingInput {
    const pads = navigator.getGamepads?.() ?? []
    let pad = this.activeIndex === null ? null : pads[this.activeIndex]
    if (!pad?.connected) {
      pad = Array.from(pads).find((candidate) => candidate?.connected && candidate.mapping === 'standard')
        ?? Array.from(pads).find((candidate) => candidate?.connected)
        ?? null
      this.activeIndex = pad?.index ?? null
    }
    if (!pad) {
      this.resetWasPressed = false
      this.startWasPressed = false
      return disconnectedInput
    }

    const resetHeld = pad.buttons[3]?.pressed ?? false
    const startHeld = pad.buttons[9]?.pressed ?? false
    const input: GamepadDrivingInput = {
      connected: true,
      id: pad.id,
      // Standard mapping axis 0 is positive right; Daylight steering is positive left.
      steer: -deadzone(pad.axes[0] ?? 0),
      drive: (pad.buttons[7]?.value ?? 0) - (pad.buttons[6]?.value ?? 0),
      lookX: Math.max(-1, Math.min(1, pad.axes[2] ?? 0)),
      lookY: Math.max(-1, Math.min(1, pad.axes[3] ?? 0)),
      handbrake: pad.buttons[0]?.pressed ?? false,
      resetPressed: resetHeld && !this.resetWasPressed,
      startPressed: startHeld && !this.startWasPressed,
      hapticsAvailable: this.hasDualRumble(pad as HapticGamepad),
    }
    this.resetWasPressed = resetHeld
    this.startWasPressed = startHeld
    return input
  }

  updateSlipHaptics(
    frontSlipDegrees: number,
    rearSlipDegrees: number,
    peakSlipDegrees: number,
    longitudinalSpeedMetersPerSecond: number,
    lateralSpeedMetersPerSecond: number,
    surface: 'asphalt' | 'grass' | 'air',
    longitudinalAccelerationMetersPerSecondSquared = 0,
    pedalDemand = 0,
    brakingDemand = 0,
    frontCombinedSlipUsage?: number,
    rearCombinedSlipUsage?: number,
  ) {
    const now = performance.now()
    if (now < this.nextContinuousHapticTime || now < this.impactHapticUntil) return
    this.nextContinuousHapticTime = now + 80

    const requestedRumble = calculateTyreRumble(
      frontSlipDegrees,
      rearSlipDegrees,
      peakSlipDegrees,
      longitudinalSpeedMetersPerSecond,
      lateralSpeedMetersPerSecond,
      surface,
      longitudinalAccelerationMetersPerSecondSquared,
      pedalDemand,
      brakingDemand,
      frontCombinedSlipUsage,
      rearCombinedSlipUsage,
    )

    const attack = 1 - Math.exp(-0.08 / 0.06)
    const release = 1 - Math.exp(-0.08 / 0.18)
    const smoothing = requestedRumble > this.smoothedTyreRumble ? attack : release
    this.smoothedTyreRumble += (requestedRumble - this.smoothedTyreRumble) * smoothing
    if (this.smoothedTyreRumble < 0.01) return

    // One non-directional tyre signal drives both actuator channels equally.
    this.playDualRumble(90, this.smoothedTyreRumble, this.smoothedTyreRumble)
  }

  playImpact(speedMetersPerSecond: number) {
    const magnitude = Math.max(0, Math.min(1, speedMetersPerSecond / 12))
    if (magnitude < 0.04) return
    this.impactHapticUntil = performance.now() + 130
    this.playDualRumble(130, magnitude, Math.min(1, magnitude * 0.65 + 0.15))
  }

  private activeGamepad() {
    if (this.activeIndex === null) return null
    return (navigator.getGamepads?.()[this.activeIndex] ?? null) as HapticGamepad | null
  }

  private hasDualRumble(pad: HapticGamepad) {
    const actuator = pad.vibrationActuator
    return Boolean(actuator && (!actuator.effects || actuator.effects.includes('dual-rumble')))
  }

  private playDualRumble(duration: number, strongMagnitude: number, weakMagnitude: number) {
    const pad = this.activeGamepad()
    const actuator = pad?.vibrationActuator
    if (!pad || !actuator || !this.hasDualRumble(pad)) return
    void actuator.playEffect('dual-rumble', {
      startDelay: 0,
      duration,
      strongMagnitude,
      weakMagnitude,
    }).catch(() => undefined)
  }
}
